// pages/book/BookSearch.tsx
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getApiUrl } from "../../api";

interface BookItem {
  book_code: string | null;
  title: string | null;
  author: string | null;
  total_count: number;
  available_count: number;
  loaned_count: number;
}

const PAGE_SIZE = 10;

export default function BookSearch() {
  const navigate = useNavigate();
  const [books, setBooks] = useState<BookItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchBooks();
  }, [page, search]);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      if (search.trim()) params.set("search", search.trim());

      const res = await fetch(
        getApiUrl(`/marubit/api/book/list?${params.toString()}`)
      );
      if (res.ok) {
        const data = await res.json();
        setBooks(data.data?.books ?? []);
        setTotal(data.data?.total ?? 0);
      }
    } catch (e) {
      console.error("도서 목록 로딩 실패:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(inputValue);
  };

  const handleClear = () => {
    setInputValue("");
    setSearch("");
    setPage(1);
    inputRef.current?.focus();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const getPageNumbers = () => {
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">도서 검색</h1>
          <p className="text-[14px] text-gray-400">총 {total}건</p>
          <p className="text-[14px] text-[#6b5248] font-medium">
            마루빛 도서관의 도서를 검색해보세요.
          </p>
        </div>

        {/* 검색 바 */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-2 max-w-[560px]">
            <div className="relative flex-1">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"
                viewBox="0 0 24 24" width={18} height={18} fill="none"
                stroke="currentColor" strokeWidth={2}
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="도서명 또는 저자를 검색하세요"
                className="w-full pl-11 pr-10 py-[11px] bg-white border border-[#e5e7eb] rounded-[10px] text-[14px] text-[#3d2f2a] outline-none focus:border-[#887267] transition-colors"
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors border-none cursor-pointer"
                >
                  <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="#888" strokeWidth={2.5}>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-6 py-[11px] bg-[#887267] text-white text-[14px] font-semibold rounded-[10px] hover:bg-[#6b5248] transition-colors border-none cursor-pointer shrink-0"
            >
              검색
            </button>
          </div>
          {search && (
            <p className="mt-2 text-[13px] text-[#887267]">
              "<span className="font-bold">{search}</span>" 검색 결과 {total}건
            </p>
          )}
        </form>

        {/* 목록 */}
        {loading ? (
          <div className="py-24 text-center text-[14px] text-gray-300">불러오는 중...</div>
        ) : books.length === 0 ? (
          <div className="bg-white rounded-2xl py-24 text-center shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            <svg className="mx-auto mb-4 text-gray-200" viewBox="0 0 24 24" width={48} height={48} fill="none" stroke="currentColor" strokeWidth={1.2}>
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            <p className="text-[15px] text-gray-300 font-medium">
              {search ? "검색 결과가 없습니다." : "등록된 도서가 없습니다."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
          {books.map((book, idx) => (
            <div
              key={book.book_code ?? idx}
              className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_8px_rgba(136,114,103,0.06)] px-6 py-4 flex items-center gap-5 hover:shadow-[0_4px_16px_rgba(136,114,103,0.12)] transition-all duration-150"
            >
              {/* 책 아이콘 */}
              <div className="w-[44px] h-[44px] rounded-[12px] bg-[#fdf7f0] border border-[#fde9c0] flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="#f5a623" strokeWidth={1.5}>
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>

              {/* 제목 / 저자 */}
              <div className="flex-1 min-w-0">
                <p className="m-0 text-[15px] font-bold text-[#3d2f2a] truncate">{book.title ?? "-"}</p>
                <p className="m-0 text-[13px] text-gray-500 mt-[2px]">{book.author ?? "저자 미상"}</p>
                {book.book_code && (
                  <p className="m-0 text-[12px] text-gray-400 mt-[2px]">{book.book_code}</p>
                )}
              </div>

              {/* 대출 현황 */}
              <div className="flex items-center gap-2 shrink-0">
                <span className={`w-2 h-2 rounded-full ${book.available_count > 0 ? "bg-green-400" : "bg-gray-300"}`} />
                <span className="text-[13px] text-gray-400">
                  {book.available_count > 0 ? "대출 가능" : "대출 중"}
                </span>
                <span className="text-[13px] text-gray-300 ml-2">
                  <span className="text-[#887267] font-bold">{book.available_count}</span>/{book.total_count}권
                </span>
              </div>


            </div>
          ))}
        </div>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >‹</button>
            {getPageNumbers().map(num => (
              <button
                key={num}
                onClick={() => setPage(num)}
                className={[
                  "w-9 h-9 flex items-center justify-center rounded-lg text-[14px] font-medium transition-colors",
                  num === page
                    ? "bg-[#887267] text-white border border-[#887267]"
                    : "border border-gray-200 text-gray-500 hover:border-[#887267] hover:text-[#887267]",
                ].join(" ")}
              >{num}</button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:border-[#887267] hover:text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >›</button>
          </div>
        )}

      </div>
    </div>
  );
}